import {
    format,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledDateTextInput,
    ControlledInput,
    ControlledPhoneNumberInput,
    FrostedSurface,
    isNonMobileWeb,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';
import z from 'zod';

import { NewMemberInfoDisclosure } from '../../../banking/components/Disclosures';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    captureException,
    track,
    trackGrowthMarketingEvent,
} from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { useVisitorId } from '../../../user/providers/VisitorProvider';
import type { User } from '../../../user/types/User';
import { resetPassword } from '../../api/AuthApi';
import { SESSION_DURATION_MINUTES } from '../../constants';
import { storeUserToken } from '../../helpers';
import { useCreateMemberWithSubscription } from '../../hooks/start-membership/useCreateMemberWithSubscription';
import { useStytch } from '../../hooks/useAuth';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import { getStytchErrorMessage } from '../../stytchHelpers';
import { continueToNextPage } from '../../utils/continueToNextPage';
import { registerDeviceToken } from '../../utils/registerDeviceToken';
import { NewMemberScreenLayout } from '../new-member/NewMemberScreenLayout';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';

const returningMemberInfoSchema = z.object({
    password: z.string().min(1, 'Please enter a password.'),
    firstName: zodFirstNameValidation,
    lastName: zodLastNameValidation,
    dateOfBirth: zodDateOfBirthValidation,
    phoneNumber: z.string().length(10, 'Please enter a valid phone number.'),
});
type ReturningMemberInfoSchema = z.infer<typeof returningMemberInfoSchema>;

export const ReturningMemberInfo = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('SignUp/ReturningMemberInfo');

    const { user } = useUser();
    const stytch = useStytch();
    const { token } = useLocalSearchParams<{ token: string }>();
    const [submitError, setSubmitError] = useState<Error | null>(null);
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    // Covers submit -> navigate; the mutation's pending flag alone isn't
    // enough since registerDeviceToken runs after it resolves.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Fallback if the reset response has no visitor id (e.g. Stytch metadata
    // wasn't set) — this device's own value, which is wrong if the member
    // checked email on a different device than they signed up on.
    const localVisitorId = useVisitorId();
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);

    const form = useForm<ReturningMemberInfoSchema>({
        resolver: zodResolver(returningMemberInfoSchema),
        defaultValues: {
            password: '',
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            phoneNumber: '',
        },
    });
    const watchedPassword = form.watch('password');

    const { createMemberWithSubscription } = useCreateMemberWithSubscription();

    const onPasswordStrengthChange = useCallback(() => {
        setSubmitError(null);
    }, []);

    const onCreateMemberSuccess = useCallback(
        async (user: User, stytchUserId: string, stytchSessionId: string) => {
            try {
                await registerDeviceToken(stytchUserId, stytchSessionId);
                setIsUserSetupInProgress(false);
                continueToNextPage();
                track(
                    AnalyticsUserEvent.SIGNUP_FUNNEL_RETURNING_MEMBER_INFO_SUBMIT,
                );
                trackGrowthMarketingEvent(
                    AnalyticsUserEvent.MEMBERSHIP_PURCHASE_PUBLISHED,
                    {
                        membershipPlanAmount: 0,
                        membershipEmail: user.email,
                        stripeCustomerId: user.stripeCustomerId ?? '',
                    },
                );
            } catch (error) {
                setIsUserSetupInProgress(false);
                setIsSubmitting(false);
                setSubmitError(
                    error instanceof Error ? error : new Error(String(error)),
                );
            }
        },
        [setIsUserSetupInProgress],
    );

    const onCreateMemberError = useCallback(
        (error: Error) => {
            setIsUserSetupInProgress(false);
            setIsSubmitting(false);
            setSubmitError(error);
        },
        [setIsUserSetupInProgress],
    );

    const onFormSubmit = useCallback(
        async (formData: ReturningMemberInfoSchema) => {
            if (token == null) {
                router.replace('/sign-in/forgot-password');
                return;
            }

            setSubmitError(null);
            setIsSubmitting(true);
            setIsUserSetupInProgress(true);

            try {
                const resetPasswordResponse = await resetPassword({
                    password: formData.password,
                    token,
                });
                const email = resetPasswordResponse.user?.emails?.[0]?.email;
                if (email == null) {
                    throw new Error(
                        'Unable to determine the account email from the password reset response.',
                    );
                }
                const visitorId =
                    resetPasswordResponse.user?.trusted_metadata?.visitor_id ??
                    localVisitorId;

                // resetPassword runs server-side only — authenticate
                // client-side too so the local Stytch session is populated.
                const authResponse = await stytch.passwords.authenticate({
                    email,
                    password: formData.password,
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });
                if (authResponse.status_code !== 200) {
                    throw new Error('Unable to sign in with the new password.');
                }
                storeUserToken(authResponse.session_jwt);

                createMemberWithSubscription({
                    email,
                    stytchId: authResponse.user_id,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: `+1${formData.phoneNumber}`,
                    dateOfBirth: `${format(formData.dateOfBirth, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`,
                    visitorId,
                    onSuccess: (user: User) => {
                        void onCreateMemberSuccess(
                            user,
                            authResponse.user_id,
                            authResponse.session.session_id,
                        );
                    },
                    onError: onCreateMemberError,
                });
            } catch (error) {
                const err =
                    error instanceof Error ? error : new Error(String(error));
                captureException(err);
                setIsUserSetupInProgress(false);
                setIsSubmitting(false);
                setSubmitError(
                    new Error(
                        getStytchErrorMessage(err) ??
                            'Something went wrong. Please try again.',
                    ),
                );
            }
        },
        [
            token,
            stytch,
            localVisitorId,
            createMemberWithSubscription,
            onCreateMemberSuccess,
            onCreateMemberError,
            setIsUserSetupInProgress,
        ],
    );

    const hasSubmitError =
        submitError?.message != null && submitError.message.length > 0;

    if (user?.isFullyAuthed) {
        return <Redirect href="/home" />;
    }

    return (
        <NewMemberScreenLayout title="Welcome back">
            <FrostedSurface padding={24} borderRadius={20}>
                <ControlledInput
                    name="password"
                    label="Password"
                    placeholder="Create a new password"
                    autoCapitalize="none"
                    autoComplete="new-password"
                    inputMode="text"
                    keyboardType="default"
                    textContentType="password"
                    secureTextEntry={!showPassword}
                    control={form.control}
                    register={form.register}
                    errors={form.formState.errors}
                    onSubmitEditing={() => {
                        form.setFocus('firstName');
                    }}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={isNonMobileWeb}
                    rightSideContent={
                        <ShowPasswordToggle
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                        />
                    }
                    disabled={isSubmitting}
                />
                <PasswordStrengthChecker
                    password={watchedPassword}
                    onValidationChange={setIsPasswordValid}
                    onStrengthCheckChange={onPasswordStrengthChange}
                />

                <XStack gap={12} marginTop={16}>
                    <YStack flex={1}>
                        <ControlledInput
                            name="firstName"
                            label="First name"
                            placeholder="First name"
                            autoCapitalize="words"
                            autoComplete="name-given"
                            textContentType="givenName"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            onSubmitEditing={() => {
                                form.setFocus('lastName');
                            }}
                            disabled={isSubmitting}
                        />
                    </YStack>
                    <YStack flex={1}>
                        <ControlledInput
                            name="lastName"
                            label="Last name"
                            placeholder="Last name"
                            autoCapitalize="words"
                            autoComplete="name-family"
                            textContentType="familyName"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            onSubmitEditing={() => {
                                form.setFocus('dateOfBirth');
                            }}
                            disabled={isSubmitting}
                        />
                    </YStack>
                </XStack>

                <View marginTop={16}>
                    <ControlledDateTextInput
                        name="dateOfBirth"
                        label="Date of birth"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={() => {
                            form.setFocus('phoneNumber');
                        }}
                        disabled={isSubmitting}
                    />
                </View>

                <View marginTop={16}>
                    <ControlledPhoneNumberInput
                        name="phoneNumber"
                        label="Mobile number"
                        autoComplete="tel-national"
                        inputMode="tel"
                        keyboardType="numbers-and-punctuation"
                        textContentType="telephoneNumber"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={form.handleSubmit(onFormSubmit)}
                        disabled={isSubmitting}
                    />
                </View>

                <View
                    animation="quick"
                    opacity={hasSubmitError ? 1 : 0}
                    height={hasSubmitError ? 48 : 0}
                    marginTop={hasSubmitError ? 16 : 0}
                    overflow="hidden"
                    animateOnly={['opacity', 'height', 'marginTop']}
                >
                    <BodySmallRegular color="$red1100">
                        {submitError?.message ?? ''}
                    </BodySmallRegular>
                </View>

                <View marginTop={16}>
                    <NewMemberInfoDisclosure />
                </View>

                <View marginTop={16}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        onPress={form.handleSubmit(onFormSubmit)}
                        disabled={
                            isSubmitting || hasSubmitError || !isPasswordValid
                        }
                        text={isSubmitting ? '' : 'Submit'}
                        icon={isSubmitting ? <Spinner /> : null}
                    />
                </View>
            </FrostedSurface>
        </NewMemberScreenLayout>
    );
};
